import { ChartDataset } from "chart.js";
import { MillifyOptions } from "millify/dist/options";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AnalyticsSummary } from "../../services/api/models";
import { decimalSeparator, localeOf } from "../../services/i18ln/format";
import { getBarOptions } from "./barOptions";

export type Metric = {
  type: "access" | "visitors" | "clicks";
  title: string;
  listType: "referrers" | "locations" | "clicks";
  listDescription: string;
};

export type Period = {
  type: "sevenDays" | "thirtyDays" | "sixMonths" | "oneYear";
  title: string;
  /** Number of chart bars: days for the short periods, months for the long ones. */
  bars: number;
  granularity: "day" | "month";
};

export type DataPresentation = {
  /** True when the backend hasn't summarized this period yet (long periods). */
  unavailable?: boolean;
  /** Undefined when there's no chart data for the period (e.g. months not summarized). */
  chart?: {
    labels: string[];
    datasets: ChartDataset[];
  };
  list: {
    count: number;
    value: string;
  }[];
  bigNumbers: {
    count: number;
    variation: {
      value: number;
      positive: boolean;
    };
  };
};

export const useMillifyOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    return {
      units: ["", t("numbers.thousand"), t("numbers.millions"), t("numbers.billion")],
      space: true,
      decimalSeparator: decimalSeparator(i18n.language),
    };
  }, [t, i18n]);
};

export const useBarOptions = (millifyOptions: Partial<MillifyOptions>) => {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  return useMemo(
    () => getBarOptions(locale, millifyOptions, day => t("pages.analytics.day", { day })),
    [locale, millifyOptions, t]
  );
};

export const useMetrics = (): Metric[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        type: "access",
        title: t("pages.analytics.metrics.access"),
        listType: "referrers",
        listDescription: t("pages.analytics.listTable.referrer"),
      },
      {
        type: "visitors",
        title: t("pages.analytics.metrics.visitors"),
        listType: "locations",
        listDescription: t("pages.analytics.listTable.location"),
      },
      {
        type: "clicks",
        title: t("pages.analytics.metrics.clicks"),
        listType: "clicks",
        listDescription: t("pages.analytics.listTable.clicks"),
      },
    ],
    [t]
  );
};

export const usePeriods = (): Period[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      { type: "sevenDays", title: t("pages.analytics.periods.sevenDays"), bars: 7, granularity: "day" },
      { type: "thirtyDays", title: t("pages.analytics.periods.thirtyDays"), bars: 30, granularity: "day" },
      { type: "sixMonths", title: t("pages.analytics.periods.sixMonths"), bars: 6, granularity: "month" },
      { type: "oneYear", title: t("pages.analytics.periods.oneYear"), bars: 12, granularity: "month" },
    ],
    [t]
  );
};

export const useDataPresentation = (
  metrics: Metric[],
  periods: Period[],
  activeMetric: number,
  activePeriod: number,
  dataLoaded: boolean,
  loadingCallback: (loading: boolean) => void,
  summary?: AnalyticsSummary
): DataPresentation | undefined => {
  const { t, i18n } = useTranslation();
  const others = t("pages.analytics.words.others");
  const locale = localeOf(i18n.language);
  return useMemo(() => {
    if (!dataLoaded) {
      return;
    }
    if (!summary) {
      loadingCallback(false);
      return undefined;
    }
    const metric = metrics[activeMetric];
    const period = periods[activePeriod];
    const periodSummary = summary.periods[period.type];

    // Long periods fall back to a "being processed" state until the backend summarizes them
    if (!periodSummary) {
      loadingCallback(false);
      return { unavailable: true, list: [], bigNumbers: { count: 0, variation: { value: 0, positive: true } } };
    }

    const entries =
      period.granularity === "day"
        ? summary.days.slice(0, period.bars).map(day => ({ label: day.date.split("-")[2], item: day }))
        : (summary.months || []).slice(0, period.bars).map(month => ({
            label: new Date(`${month.month}-01T12:00:00`).toLocaleDateString(locale, { month: "short" }),
            item: month,
          }));
    const orderedEntries = [...entries].reverse();

    const chart = orderedEntries.length
      ? {
          labels: orderedEntries.map(entry => entry.label),
          datasets: [
            {
              data: orderedEntries.map(entry => entry.item[metric.type]),
              backgroundColor: "#a3ffc8",
              borderWidth: 0,
              minBarLength: 1,
            },
          ],
        }
      : undefined;

    const bigNumbers = periodSummary.bigNumbers[metric.type];

    const list = periodSummary[metric.listType].map((item: { value: string; count: number }) => {
      let value = item.value || others;
      if (value === "none") {
        value = others;
      }

      return {
        value,
        count: item.count,
      };
    });

    loadingCallback(false);

    return {
      chart,
      list,
      bigNumbers,
    };
  }, [activeMetric, activePeriod, loadingCallback, dataLoaded, metrics, periods, summary, others, locale]);
};
